/**
 * Shared chart-grid layout shape.
 *
 * Originally lived inline in `features/multi-chart/multi-chart-state.ts`. Lifted
 * here so the backtest module can render an NxM grid of `BacktestChart` cells
 * using the same picker, the same presets, and the same `{ rows, cols }`
 * contract — no drift between the dashboard's multi-chart panel and the
 * backtest editor's chart grid.
 *
 * `GridDim` is intentionally constrained to 1–3 on each axis. That caps the
 * cell count at 9, which keeps the render cost bounded and matches the
 * existing multi-chart preset list verbatim.
 */
import type { ChartSyncTogglesValue } from '@edge/charts'

export type GridDim = 1 | 2 | 3

/**
 * Explicit placement for a single cell within an asymmetric grid. Coordinates
 * are 1-based CSS grid lines; spans default to 1. Used by presets like
 * "1 top / 2 bottom" where the cells don't tile uniformly.
 */
export interface ChartLayoutCell {
  rowStart: number
  colStart: number
  rowSpan?: number
  colSpan?: number
}

export interface MultiChartLayout {
  rows: GridDim
  cols: GridDim
  /**
   * Explicit per-cell placement for asymmetric layouts. When present, the
   * grid still spans `rows`×`cols` tracks but the cell count and positions
   * come from `cells` (not `rows*cols`). Absent → uniform tiling.
   */
  cells?: readonly ChartLayoutCell[]
  /**
   * Stable identity for a preset. Required for asymmetric presets because
   * several of them share the same `rows`/`cols` (all 2×2) and would
   * otherwise be indistinguishable in active-state matching.
   */
  id?: string
  /** Human-readable label for pickers/tooltips (e.g. "1 top / 2 bottom"). */
  label?: string
  /**
   * Per-split-group percentages from drag-resizing (react-resizable-panels
   * `onLayout` output), keyed by `SplitNode.key`. Absent = equal split. Cleared
   * whenever a preset is picked, because the picker replaces the whole object.
   * Unvalidated on read (the workspace doc stores panel state as unknown), so
   * consumers go through `normalizeLayoutSizes`.
   */
  sizes?: Readonly<Record<string, readonly number[]>>
}

/**
 * Ordered for the picker (1-cell → 2-cell → 3-cell → 4-cell → 6-cell → 9-cell).
 * Mirrors the dashboard's multi-chart picker so users see the same options
 * everywhere a grid layout is offered.
 */
export const LAYOUT_PRESETS: readonly MultiChartLayout[] = [
  { rows: 1, cols: 1 },
  { rows: 1, cols: 2 },
  { rows: 2, cols: 1 },
  { rows: 1, cols: 3 },
  { rows: 3, cols: 1 },
  // Asymmetric 3-cell layouts — one full-span cell plus a stacked pair.
  {
    rows: 2,
    cols: 2,
    id: '1t2b',
    label: '1 top / 2 bottom',
    cells: [
      { rowStart: 1, colStart: 1, colSpan: 2 },
      { rowStart: 2, colStart: 1 },
      { rowStart: 2, colStart: 2 },
    ],
  },
  {
    rows: 2,
    cols: 2,
    id: '2t1b',
    label: '2 top / 1 bottom',
    cells: [
      { rowStart: 1, colStart: 1 },
      { rowStart: 1, colStart: 2 },
      { rowStart: 2, colStart: 1, colSpan: 2 },
    ],
  },
  {
    rows: 2,
    cols: 2,
    id: '1l2r',
    label: '1 left / 2 right',
    cells: [
      { rowStart: 1, colStart: 1, rowSpan: 2 },
      { rowStart: 1, colStart: 2 },
      { rowStart: 2, colStart: 2 },
    ],
  },
  {
    rows: 2,
    cols: 2,
    id: '1r2l',
    label: '1 right / 2 left',
    cells: [
      { rowStart: 1, colStart: 1 },
      { rowStart: 2, colStart: 1 },
      { rowStart: 1, colStart: 2, rowSpan: 2 },
    ],
  },
  { rows: 2, cols: 2 },
  { rows: 2, cols: 3 },
  { rows: 3, cols: 2 },
  { rows: 3, cols: 3 },
] as const

/** Default starting layout — single cell, matches the pre-grid behaviour. */
export const DEFAULT_CHART_LAYOUT: MultiChartLayout = { rows: 1, cols: 1 }

/**
 * "Sync in layout" defaults shared by the dashboard Chart Scanner + History's
 * Chart Grid panel (only the DASHBOARD panel is the "Chart Scanner"):
 * Interval ON (the panel-level Toolbar drives every cell), Symbol OFF (each cell
 * is a different scanner ticker), same-ticker crosshair ON, global crosshair +
 * date-range OFF. One source of truth so the two scanners can't drift.
 */
export const GRID_SYNC_DEFAULTS: ChartSyncTogglesValue = {
  symbol: false,
  interval: true,
  crosshair: true,
  crosshairGlobal: false,
  time: false,
  window: false,
}

/** Drill-down sync defaults — Symbol locked-true (every cell = the drilled
 *  ticker/event), Interval unlocked-false (compare timeframes side-by-side). */
export const GRID_DRILLED_SYNC_DEFAULTS: ChartSyncTogglesValue = {
  symbol: true,
  interval: false,
  crosshair: true,
  crosshairGlobal: false,
  time: false,
  window: false,
}

/** Total cell count for a layout — explicit `cells` win over uniform tiling. */
export function cellCountOf(layout: MultiChartLayout): number {
  return layout.cells ? layout.cells.length : layout.rows * layout.cols
}

/**
 * Index of the visual bottom-left cell — the one whose corner sits nearest the
 * grid's bottom-left. Used to render the single Edge watermark once per grid.
 *
 * Uniform layouts auto-flow row-major, so it's the first cell of the last row
 * (`(rows - 1) * cols`). Asymmetric presets place cells explicitly and their
 * array order is not row-major, so pick the cell reaching the lowest row
 * (largest bottom edge = `rowStart + rowSpan`), breaking ties toward the
 * leftmost (`colStart`). This is why `(rows - 1) * cols` alone misfires on
 * presets like `1t2b` (it lands on the bottom-RIGHT cell).
 */
export function bottomLeftCellIndex(layout: MultiChartLayout): number {
  const cells = layout.cells
  if (!cells || cells.length === 0) {
    return (layout.rows - 1) * layout.cols
  }
  let best = 0
  let bestBottom = -Infinity
  let bestLeft = Infinity
  cells.forEach((cell, index) => {
    const bottom = cell.rowStart + (cell.rowSpan ?? 1) // exclusive bottom track
    const left = cell.colStart
    if (bottom > bestBottom || (bottom === bestBottom && left < bestLeft)) {
      bestBottom = bottom
      bestLeft = left
      best = index
    }
  })
  return best
}

/**
 * CSS grid placement for cell `index`. Returns `{}` for uniform layouts (the
 * browser auto-flows them) and explicit `gridColumn`/`gridRow` for asymmetric
 * presets so the spanning cell lands in the right tracks.
 */
export function cellGridStyle(
  layout: MultiChartLayout,
  index: number,
): { gridColumn?: string; gridRow?: string } {
  const cell = layout.cells?.[index]
  if (!cell) return {}
  return {
    gridColumn: `${cell.colStart} / span ${cell.colSpan ?? 1}`,
    gridRow: `${cell.rowStart} / span ${cell.rowSpan ?? 1}`,
  }
}

/**
 * Equality for active-state matching. Asymmetric presets carry an `id` (they
 * share rows/cols and would otherwise collide); uniform presets match by
 * rows/cols. A layout with an id never matches one without.
 */
export function layoutsEqual(a: MultiChartLayout, b: MultiChartLayout): boolean {
  if (a.id || b.id) return a.id === b.id
  return a.rows === b.rows && a.cols === b.cols
}

/** Stable label for a preset — used in pickers, telemetry, and tests. */
export function labelOfLayout(layout: MultiChartLayout): string {
  return layout.label ?? `${layout.rows}×${layout.cols}`
}

// ─── Split tree (drag-resizable cells) ───

export type SplitDirection = 'horizontal' | 'vertical'

/**
 * A layout as nested resizable groups. Leaves carry the cell index so cell
 * keys stay `${panelId}::${i}`; group keys index `MultiChartLayout.sizes`.
 */
export type SplitNode =
  | { kind: 'leaf'; index: number }
  | { kind: 'group'; key: string; direction: SplitDirection; children: readonly SplitNode[] }

/**
 * Uniform grids with both axes > 1 use two shared keys: the root `rows` group
 * and one `cols` key for EVERY row group, so a drag in one row is mirrored into
 * the others and the vertical dividers stay aligned.
 */
const UNIFORM_ROWS_KEY = 'rows'
const UNIFORM_COLS_KEY = 'cols'

/**
 * Split tree for a layout. Uniform grids are rows-of-cols; asymmetric presets
 * are cut along the first full-width (then full-height) track boundary and
 * recursed. A 1×1 layout is a bare leaf; callers keep their single-chart path.
 * Throws when no guillotine cut exists; every shipped preset resolves (tested).
 */
export function splitTreeOf(layout: MultiChartLayout): SplitNode {
  if (!layout.cells) return uniformTree(layout.rows, layout.cols)
  const boxes: Box[] = layout.cells.map((c, index) => ({
    index,
    r0: c.rowStart,
    r1: c.rowStart + (c.rowSpan ?? 1),
    c0: c.colStart,
    c1: c.colStart + (c.colSpan ?? 1),
  }))
  return cutBoxes(boxes, 'root')
}

function leafOf(index: number): SplitNode {
  return { kind: 'leaf', index }
}

function range(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i)
}

function uniformTree(rows: GridDim, cols: GridDim): SplitNode {
  if (rows === 1 && cols === 1) return leafOf(0)
  if (rows === 1) {
    return {
      kind: 'group',
      key: 'root',
      direction: 'horizontal',
      children: range(cols).map(leafOf),
    }
  }
  if (cols === 1) {
    return { kind: 'group', key: 'root', direction: 'vertical', children: range(rows).map(leafOf) }
  }
  return {
    kind: 'group',
    key: UNIFORM_ROWS_KEY,
    direction: 'vertical',
    children: range(rows).map((r) => ({
      kind: 'group',
      key: UNIFORM_COLS_KEY,
      direction: 'horizontal',
      children: range(cols).map((c) => leafOf(r * cols + c)),
    })),
  }
}

/** A placed cell as half-open track ranges (`r0 <= row < r1`). */
interface Box {
  index: number
  r0: number
  r1: number
  c0: number
  c1: number
}

function cutBoxes(boxes: Box[], key: string): SplitNode {
  if (boxes.length === 1) return leafOf(boxes[0].index)
  const hCut = firstCut(boxes, 'r0', 'r1')
  if (hCut !== null) return groupAt(boxes, key, 'vertical', (b) => b.r1 <= hCut)
  const vCut = firstCut(boxes, 'c0', 'c1')
  if (vCut !== null) return groupAt(boxes, key, 'horizontal', (b) => b.c1 <= vCut)
  throw new Error(`chart-layout: no guillotine cut for layout group ${key}`)
}

/** Smallest track boundary strictly inside the boxes' extent that no box spans. */
function firstCut(boxes: Box[], start: 'r0' | 'c0', end: 'r1' | 'c1'): number | null {
  const lo = Math.min(...boxes.map((b) => b[start]))
  const hi = Math.max(...boxes.map((b) => b[end]))
  for (let t = lo + 1; t < hi; t++) {
    if (boxes.every((b) => b[end] <= t || b[start] >= t)) return t
  }
  return null
}

function groupAt(
  boxes: Box[],
  key: string,
  direction: SplitDirection,
  before: (b: Box) => boolean,
): SplitNode {
  const a = boxes.filter(before)
  const b = boxes.filter((x) => !before(x))
  return {
    kind: 'group',
    key,
    direction,
    children: [cutBoxes(a, `${key}.0`), cutBoxes(b, `${key}.1`)],
  }
}

/** Group key → child count, for validating stored sizes. Empty for 1×1. */
export function groupKeysOf(layout: MultiChartLayout): Record<string, number> {
  const out: Record<string, number> = {}
  const walk = (n: SplitNode) => {
    if (n.kind !== 'group') return
    out[n.key] = n.children.length
    n.children.forEach(walk)
  }
  walk(splitTreeOf(layout))
  return out
}

/** `n` equal percentages summing to 100. */
export function equalSplit(n: number): number[] {
  return Array.from({ length: n }, () => 100 / n)
}

/**
 * Keep only size arrays whose key exists for this layout, whose length matches
 * the group's child count, and whose entries are finite and non-negative.
 * `panels` in the workspace doc is `z.record(z.unknown())`, so nothing else
 * validates this field. Returns fresh arrays; undefined when nothing survives.
 */
export function normalizeLayoutSizes(
  layout: MultiChartLayout,
  sizes: Readonly<Record<string, readonly number[]>> | undefined,
): Record<string, number[]> | undefined {
  if (!sizes) return undefined
  const arity = groupKeysOf(layout)
  const out: Record<string, number[]> = {}
  for (const [k, v] of Object.entries(sizes)) {
    if (!Array.isArray(v) || arity[k] !== v.length) continue
    if (!v.every((x) => typeof x === 'number' && Number.isFinite(x) && x >= 0)) continue
    out[k] = [...v]
  }
  return Object.keys(out).length ? out : undefined
}

/** Shape identity: preset id or rows×cols. Never includes sizes. */
export function layoutShapeKey(layout: MultiChartLayout): string {
  return layout.id ?? `${layout.rows}x${layout.cols}`
}
